import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import Demo from '@/components/Demo';
import DemoContext from '@/components/Demo/DemoContext';
import Paper from '@/components/Paper';
import Paragraph from '@/components/Paragraph';
import InlineCode from '@/components/InlineCode';
import FadeOnChange from '@/components/FadeOnChange';
import ToggleButtonGroupControl from '@/components/Controls/ToggleButtonGroupControl';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';

import { CODE_BACKGROUND } from './index.constants';
import { INITIAL_VALUES, constructSnippet } from './PickTwo.helpers';

function PickTwo({ codeOutput }) {
  return (
    <Demo
      initialValues={{
        values: INITIAL_VALUES,
      }}
      controlPosition="top"
      customGrid={{ gridTemplateColumns: '1fr' }}
      outputWrapper={null}
      controls={[<ToggleGroup key="values" id="values" />]}
      renderAfter={({ values }) => (
        <SummaryParagraph values={values} />
      )}
    >
      {({ values }) => {
        return (
          <>
            <Row>
              <Column>
                <CodeSnippetProvider
                  lang="css"
                  code={constructSnippet(values)}
                >
                  {codeOutput}
                </CodeSnippetProvider>
              </Column>
              <DemoColumn>
                <Container>
                  <Background>
                    <FakeHeading>
                      {[3, 5, 1, 4, 7].map((width, index) => (
                        <HeadingBlock
                          key={index}
                          style={{ width: `${width}rem` }}
                        />
                      ))}
                    </FakeHeading>
                    <FakeParagraph>
                      {[
                        1, 4, 1.5, 2, 4.75, 0.5, 4, 7, 1.25, 1.5,
                        2.25, 1, 5, 0.5, 2.1, 0.785, 2.5, 4.2, 3.5, 1,
                      ].map((width, index) => (
                        <Block
                          key={index}
                          style={{ width: `${width}rem` }}
                        />
                      ))}
                    </FakeParagraph>
                    <FakeParagraph>
                      {[
                        0.785, 4.2, 1.25, 4, 1.5, 1.5, 1, 2.25, 0.5,
                        2.1, 2.5, 3.5, 1,
                      ].map((width, index) => (
                        <Block
                          key={index}
                          style={{ width: `${width}rem` }}
                        />
                      ))}
                    </FakeParagraph>
                  </Background>
                  <Modal
                    layout
                    style={{
                      left: values.includes('left: 0px')
                        ? 0
                        : undefined,
                      right: values.includes('right: 0px')
                        ? 0
                        : undefined,
                      width: values.includes('width: 12rem')
                        ? '12rem'
                        : undefined,
                    }}
                  />
                </Container>
              </DemoColumn>
            </Row>
          </>
        );
      }}
    </Demo>
  );
}

function SummaryParagraph({ values }) {
  let p;
  if (values.includes('left: 0px') && values.includes('right: 0px')) {
    p = (
      <>
        If we want the element to be <InlineCode>0px</InlineCode> from
        the left edge, and <InlineCode>0px</InlineCode> from the right
        edge, it will stretch to the full width of the viewport, which
        is wider than <InlineCode>12rem</InlineCode>.
      </>
    );
  } else if (
    values.includes('left: 0px') &&
    values.includes('width: 12rem')
  ) {
    p = (
      <>
        We can anchor the element to the left edge, and have it be
        <InlineCode>12rem</InlineCode> wide, but then it won't be{' '}
        <InlineCode>0px</InlineCode> from the right edge.
      </>
    );
  } else if (
    values.includes('right: 0px') &&
    values.includes('width: 12rem')
  ) {
    p = (
      <>
        We can stick the element to the right edge with a{' '}
        <InlineCode>12rem</InlineCode> width, but this will pull it
        away from the left edge.
      </>
    );
  }

  if (p) {
    return (
      <FadeOnChange>
        <Paragraph>{p}</Paragraph>
      </FadeOnChange>
    );
  }
}

function ToggleGroup({ id }) {
  const {
    id: demoUnitId,
    state,
    setState,
  } = React.useContext(DemoContext);

  const value = state[id];

  // Ensure page-wide uniqueness by mixing in the demo unit's ID.
  const htmlId = `${demoUnitId}_${id}`;

  // We're going to store the state as an array, since that lets us track the order. But the `value` for the control needs to be an object.
  const formattedValue = value.reduce((acc, item) => {
    acc[item] = true;
    return acc;
  }, {});

  return (
    <ToggleButtonGroupControl
      id={htmlId}
      label="Pick two"
      options={['left: 0px', 'right: 0px', 'width: 12rem']}
      value={formattedValue}
      setValue={(newValue) => {
        let nextValue;
        if (value.includes(newValue)) {
          nextValue = value.filter((item) => item !== newValue);
        } else {
          nextValue = [...value, newValue];
        }
        if (nextValue.length > 2) {
          nextValue.shift();
        }

        setState((state) => ({
          ...state,
          [id]: nextValue,
        }));
      }}
    />
  );
}

const Row = styled.div`
  display: flex;
  gap: 24px;
  padding-bottom: 16px;

  @media (max-width: 34rem) {
    flex-direction: column-reverse;
  }
`;
const Column = styled.div`
  position: relative;
  flex: 1;
  background: ${CODE_BACKGROUND};
  border-radius: 8px;
  height: 230px;
`;

const DemoColumn = styled(Column)`
  display: flex;
  justify-content: center;
  overflow: hidden;
  overflow: clip;
`;

const Container = styled(Paper)`
  position: relative;
  background: hsl(0deg 0% 90%);
  color-scheme: light;
`;

const Background = styled.div`
  overflow: hidden;
  overflow: clip;
  padding: 16px;

  max-height: 100%;
`;

const FakeHeading = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 1.5em;
`;
const FakeParagraph = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 1em;
`;

const Block = styled.span`
  background: hsl(0deg 0% 80%);
  border-radius: 2px;
  height: 0.75rem;
`;
const HeadingBlock = styled(Block)`
  height: 1rem;
  background: hsl(0deg 0% 70%);
`;

const Modal = styled(motion.div)`
  position: absolute;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  min-width: 32px;
  margin: auto;
  background: white;
  box-shadow:
    0px 1px 2px hsl(0deg 0% 0% / 0.2),
    0px 2px 4px hsl(0deg 0% 0% / 0.2),
    0px 4px 8px hsl(0deg 0% 0% / 0.2);
  border-radius: 2px;
`;

export default PickTwo;
